import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';

const Hero = () => {
  const heroRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ delay: 0.15 });
      tl.from('.h-badge', { opacity: 0, y: 16, duration: 0.6, ease: 'power3.out' })
        .from('.h-title', { opacity: 0, y: 40, duration: 0.9, ease: 'power3.out' }, '-=0.2')
        .from('.h-desc', { opacity: 0, y: 24, duration: 0.7, ease: 'power3.out' }, '-=0.4')
        .from('.h-cta', { opacity: 0, y: 16, duration: 0.6, stagger: 0.12, ease: 'power3.out' }, '-=0.3')
        .from('.h-stat', { opacity: 0, y: 16, duration: 0.5, stagger: 0.08, ease: 'power3.out' }, '-=0.2')
        .from('.h-img', { opacity: 0, scale: 0.97, duration: 0.9, ease: 'power3.out' }, '-=0.8');
    }, heroRef);
    return () => ctx.revert();
  }, []);

  const stats = [
    { value: '12+', label: 'Yıl Deneyim', icon: 'ri-time-line' },
    { value: '400+', label: 'Proje', icon: 'ri-rocket-line' },
    { value: '7/24', label: 'Destek', icon: 'ri-headphone-line' },
    { value: '3', label: 'Lider Platform', icon: 'ri-medal-line' },
  ];

  return (
    <section ref={heroRef} className="relative min-h-screen bg-white overflow-hidden flex items-center">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle, #E2E8F0 1px, transparent 1px)', backgroundSize: '32px 32px', opacity: 0.5 }} />
        <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(8,145,178,0.08) 0%, transparent 70%)' }} />
        <div className="absolute bottom-0 left-0 w-[500px] h-[400px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(249,115,22,0.06) 0%, transparent 70%)' }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 py-32 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left — content */}
          <div>
            <div className="h-badge inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#ECFEFF] border border-[#0891B2]/30 text-[#0E7490] text-xs font-semibold mb-6 font-body">
              <span className="w-1.5 h-1.5 rounded-full bg-[#0891B2] animate-pulse"></span>
              Resmi ManageEngine Partner — Türkiye
            </div>

            <h1 className="h-title text-5xl md:text-6xl font-extrabold text-[#0F172A] leading-[1.08] mb-6 font-heading">
              Kurumsal IT'yi<br />
              <span className="text-[#0891B2]">Dönüştürüyoruz</span>
            </h1>

            <p className="h-desc text-lg text-[#475569] leading-relaxed mb-8 max-w-lg font-body">
              <strong className="text-[#0F172A]">ManageEngine</strong>, <strong className="text-[#0F172A]">AutomMate</strong> ve <strong className="text-[#0F172A]">Zoho ONE</strong> çözümleriyle IT operasyonlarınızı modernleştirin, otomatikleştirin ve daha verimli yönetin.
            </p>

            <div className="flex flex-wrap gap-3 mb-12">
              <a href="#contact" className="h-cta inline-flex items-center gap-2 px-6 py-3.5 bg-[#0891B2] text-white font-semibold rounded-lg hover:bg-[#0E7490] transition-all duration-200 hover:scale-105 whitespace-nowrap cursor-pointer font-body">
                <i className="ri-calendar-check-line"></i>
                Demo Talep Et
              </a>
              <a href="#solutions" className="h-cta inline-flex items-center gap-2 px-6 py-3.5 bg-white text-[#0F172A] font-semibold rounded-lg border border-[#E2E8F0] hover:border-[#0891B2]/40 hover:text-[#0891B2] transition-all duration-200 whitespace-nowrap cursor-pointer font-body">
                Çözümleri Keşfet
                <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1"></i>
              </a>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {stats.map((s, i) => (
                <div key={i} className="h-stat text-center p-3 rounded-xl bg-[#F8FAFC] border border-[#E2E8F0]">
                  <div className="w-8 h-8 flex items-center justify-center mx-auto mb-1.5 rounded-lg bg-[#ECFEFF]">
                    <i className={`${s.icon} text-[#0891B2] text-base`}></i>
                  </div>
                  <div className="text-xl font-extrabold text-[#0F172A] font-heading">{s.value}</div>
                  <div className="text-xs text-[#64748B] font-body">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — image */}
          <div className="h-img relative">
            <div className="relative rounded-2xl overflow-hidden border border-[#E2E8F0] bg-[#F8FAFC]">
              <img
                src="https://public.readdy.ai/ai/img_res/8f0d61d3baa4ca0378aea1e80ac7d383.jpg"
                alt="WOTECH IT Dashboard"
                title="Kurumsal IT Yönetim Platformu"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-white/20 to-transparent" />
            </div>

            {/* Floating cards */}
            <div className="absolute -left-6 bottom-16 px-4 py-3 bg-white rounded-xl border border-[#E2E8F0] flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#ECFEFF]">
                <i className="ri-shield-check-line text-[#0891B2] text-lg"></i>
              </div>
              <div>
                <div className="text-xs font-bold text-[#0F172A] font-heading">Güvenli & Uyumlu</div>
                <div className="text-xs text-[#64748B] font-body">ISO 27001 standartları</div>
              </div>
            </div>

            <div className="absolute -right-4 top-12 px-4 py-3 bg-white rounded-xl border border-[#E2E8F0] flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#FFF7ED]">
                <i className="ri-rocket-2-line text-[#F97316] text-lg"></i>
              </div>
              <div>
                <div className="text-xs font-bold text-[#0F172A] font-heading">Hızlı Kurulum</div>
                <div className="text-xs text-[#64748B] font-body">Ortalama 2 hafta</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
